import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthForm } from "@/components/auth/AuthForm";
import { useAuthFlow } from "@/hooks/auth/useAuthFlow";
import { useSessionCheck } from "@/hooks/useSessionCheck";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/ui/language-selector";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Play } from "lucide-react";
import Footer from "@/components/layout/Footer";
import logoGolf from "@/assets/logo-golf.webp";
import golfBallNearHole from "@/assets/golf-ball-near-hole.webp";
import golfBallOnGreen from "@/assets/golf-ball-on-green.webp";
import SEO from "@/components/seo/SEO";
const Auth = () => {
  const {
    email,
    setEmail,
    password,
    setPassword,
    username,
    setUsername,
    lastName,
    setLastName,
    firstName,
    setFirstName,
    residenceCountry,
    setResidenceCountry,
    loading,
    isSignUp,
    setIsSignUp,
    handleAuth,
    handleResetPassword
  } = useAuthFlow();
  
  const { t } = useTranslation(['auth', 'presentation']);
  const navigate = useNavigate();
  
  useSessionCheck();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white flex flex-col">
      <SEO
        title={isSignUp ? "Inscription — Golf Progress Calculator" : "Connexion — Golf Progress Calculator"}
        description="Connectez-vous ou créez votre compte Golf Progress Calculator et profitez d'un mois d'essai offert pour suivre votre progression."
        path="/auth"
        noindex
      />
      <div className="fixed top-4 left-4 z-10">
        <img src={logoGolf} alt="Logo Golf" className="h-12 md:h-16 w-auto" width="64" height="64" fetchPriority="high" />
      </div>
      <div className="fixed top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      <div className="container mx-auto px-4 pt-20 pb-8 flex flex-col lg:flex-row items-center gap-12 flex-1">
        {/* Section de présentation */}
        <div className="lg:w-1/2 space-y-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <img src={golfBallNearHole} alt="Balle de golf près du trou" className="w-full h-48 object-cover rounded-lg shadow-lg" width="310" height="207" fetchPriority="high" />
            <img src={golfBallOnGreen} alt="Balle de golf sur le green avec golfeurs" className="w-full h-48 object-cover rounded-lg shadow-lg" width="310" height="207" loading="lazy" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900">{t('presentation.title')}</h1>
          <div className="space-y-4">
            <p className="text-lg text-gray-700">{t('presentation.subtitle')}</p>
            
            {/* Encadré essai gratuit */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4 flex items-center justify-between gap-6">
              <p className="text-xl font-bold text-green-800 text-left flex-1">
                {t('auth:freeTrialOffer')} <span className="text-sm font-normal">{t('auth:freeTrialOfferSmall')}</span>
              </p>
              <img src="/lovable-uploads/qr-code-scan-me.png" alt="QR Code" className="w-32 h-32 flex-shrink-0" width="128" height="128" loading="lazy" />
            </div>
            
            {/* Bouton découvrir en vidéo */}
            <Button 
              variant="outline" 
              onClick={() => navigate('/presentation')}
              className="w-full border-golf-300 text-golf-700 hover:bg-golf-50"
            >
              <Play className="mr-2 h-4 w-4" />
              {t('presentation:discoverButton')}
            </Button>
            
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-gray-700">
                <span className="bg-green-100 p-1 rounded-full">✓</span>
                {t('presentation.features.intuitiveInterface')}
              </li>
              <li className="flex items-center gap-2 text-gray-700">
                <span className="bg-green-100 p-1 rounded-full">✓</span>
                {t('presentation.features.sevenSituations')}
              </li>
              <li className="flex items-center gap-2 text-gray-700">
                <span className="bg-green-100 p-1 rounded-full">✓</span>
                {t('presentation.features.realTimeProgress')}
              </li>
              <li className="flex items-center gap-2 text-gray-700">
                <span className="bg-green-100 p-1 rounded-full">✓</span>
                {t('presentation.features.detailedStats')}
              </li>
            </ul>
          </div>
        </div>

        {/* Section du formulaire */}
        <div className="lg:w-1/2 w-full flex justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{isSignUp ? t('titles.signup') : t('titles.login')}</CardTitle>
              <CardDescription>
                {isSignUp ? t('presentation.descriptions.signup') : t('presentation.descriptions.login')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AuthForm 
                email={email} 
                setEmail={setEmail} 
                password={password} 
                setPassword={setPassword} 
                username={username}
                setUsername={setUsername}
                lastName={lastName}
                setLastName={setLastName}
                firstName={firstName}
                setFirstName={setFirstName}
                residenceCountry={residenceCountry}
                setResidenceCountry={setResidenceCountry}
                loading={loading} 
                isSignUp={isSignUp} 
                setIsSignUp={setIsSignUp} 
                handleAuth={handleAuth}
                handleResetPassword={handleResetPassword}
              />
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Auth;
